import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useState, useEffect, useMemo } from 'react'
import { notification } from 'antd'
import { ConfirmModal } from '@/components/ui/confirm-modal'
import { PageHeader } from '@/components/ui/page-header'
import type { PaginationState } from '@tanstack/react-table'
import { getCoreRowModel, getPaginationRowModel } from '@tanstack/react-table'
import { DataTable } from '@/components/ui/data-table'
import { Pagination } from '@/components/ui/pagination'
import { fetchRobots, updateRobot, deleteRobot } from '@/api/robots.api'
import { DrawerEditRobot } from './RobotEdit'
import type { Robot } from '@/types/api'
import { buildColumns } from './columns'
import { RobotFilterPanel } from './RobotFilterPanel'
import type { RobotFilterValues } from './RobotFilterPanel'
import { RobotDetailDrawer } from './RobotDrawer'
import { useAtomValue } from 'jotai'
import { currentUserAtom } from '@/store/auth.atom'
import { formatStartDate, formatEndDate } from '@/utils/format'
import { usePermission } from '@/hooks/use-permission'
import { exportRobots, useTableExport } from '@/api/dowload-excel.api'

const DEFAULT_FILTER: RobotFilterValues = {
  keyword: '',
  unit: undefined,
  unitName: undefined,
  status: undefined,
  time: undefined,
  deleted: undefined,
}

export function RobotsPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const currentUser = useAtomValue(currentUserAtom)
  const showUnitColumn = currentUser?.userType === 1

  const [notificationApi, contextHolder] = notification.useNotification()
  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  })
  const [loadingRobotId, setLoadingRobotId] = useState<string | null>(null)
  const [pendingStatus, setPendingStatus] = useState<number | null>(null)
  const [pendingRobot, setPendingRobot] = useState<Robot | null>(null)
  const [deletingRobot, setDeletingRobot] = useState<Robot | null>(null)

  const [showFilter, setShowFilter] = useState(false)
  const [filter, setFilter] = useState<RobotFilterValues>(DEFAULT_FILTER)
  const [activeFilters, setActiveFilters] =
    useState<RobotFilterValues>(DEFAULT_FILTER)
  const [viewingRobotId, setViewingRobotId] = useState<string | null>(null)
  const [editingRobotId, setEditingRobotId] = useState<string | null>(null)
  const [isExporting, setIsExporting] = useState(false)
  const canView = usePermission('robot-view')
  const canUpdate = usePermission('robot-update')
  const canExport = usePermission('robot-export')

  const filterParams = useMemo(() => {
    const startDate = activeFilters.time?.from
      ? formatStartDate(activeFilters.time.from)
      : undefined

    const endDateSource = activeFilters.time?.to || activeFilters.time?.from
    const endDate = endDateSource ? formatEndDate(endDateSource) : undefined

    const unitId = showUnitColumn ? activeFilters.unit : currentUser?.unitId

    return {
      page: pageIndex,
      limit: pageSize,
      robotId: activeFilters.keyword || undefined,
      unitId: unitId || undefined,
      status: activeFilters.status,
      startDate,
      endDate,
      deleted: activeFilters.deleted,
    }
  }, [pageIndex, pageSize, activeFilters, showUnitColumn, currentUser?.unitId])

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['robots', pageIndex, pageSize, activeFilters],
    queryFn: ({ signal }) =>
      fetchRobots(
        pageIndex,
        pageSize,
        {
          robotId: filterParams.robotId,
          unitId: filterParams.unitId,
          status: filterParams.status,
          deleted: filterParams.deleted,
          startDate: filterParams.startDate,
          endDate: filterParams.endDate,
        },
        signal,
      ),
    refetchOnMount: 'always',
    staleTime: 0,
    gcTime: 0,
  })
  const hasData = Boolean(data?.items && data.items.length > 0)

  const handleToggleStatus = (robot: Robot) => {
    setPendingRobot(robot)
  }

  const { mutate: toggleRobotStatus } = useMutation({
    mutationFn: (robot: Robot) => {
      setLoadingRobotId(robot.id)
      setPendingStatus(robot.status ?? null)
      return updateRobot(robot.id, {
        status: robot.status,
        name: robot.name,
        unitId: robot.unitResponse?.id,
        diagramId: robot.diagramInfo?.id,
      })
    },
    onSuccess: () => {
      notificationApi.success({
        title: 'Cập nhật trạng thái robot thành công!',
      })
      queryClient.invalidateQueries({ queryKey: ['robots'] })
    },
    onError: () => {
      setLoadingRobotId(null)
      setPendingStatus(null)
    },
  })

  // Mutation xử lý xóa robot
  const { mutate: removeRobot, isPending: isDeleting } = useMutation({
    mutationFn: (id: string) => deleteRobot(id),
    onSuccess: () => {
      notificationApi.success({
        title: 'Xóa robot thành công!',
      })
      setDeletingRobot(null)
      queryClient.invalidateQueries({ queryKey: ['robots'] })
    },
  })

  // Ẩn Spin chỉ khi trạng thái robot đã đổi thành công trên UI
  useEffect(() => {
    if (!loadingRobotId || pendingStatus === null) return
    const found = data?.items.find((r) => r.id === loadingRobotId)
    if (found && found.status === pendingStatus) {
      setLoadingRobotId(null)
      setPendingStatus(null)
    }
  }, [data, loadingRobotId, pendingStatus])

  const columns = useMemo(
    () =>
      buildColumns({
        pageIndex,
        pageSize,
        loadingRobotId,
        showUnitColumn,
        canView,
        canUpdate,
        onViewDetail: async (robot) => {
          setViewingRobotId(robot.id)
        },
        onSetupLocation: (robot) => {
          navigate({
            to: `/robots/${robot.id}/position`,
          })
        },
        onUpdate: (robot) => {
          setEditingRobotId(robot.id)
        },
        onDelete: (robot) => {
          setDeletingRobot(robot)
        },
        onToggleStatus: handleToggleStatus,
      }),
    [
      pageIndex,
      pageSize,
      loadingRobotId,
      showUnitColumn,
      canView,
      canUpdate,
      navigate,
    ],
  )

  const { handleExport } = useTableExport<Robot>(
    'Danh sách robot',
    columns,
    async (payload) => {
      try {
        setIsExporting(true)
        await exportRobots(filterParams, payload, 'danh-sach-robot.xlsx')
      } finally {
        setIsExporting(false)
      }
    },
  )

  if (isLoading) {
    return (
      <section className="space-y-4">
        <PageHeader title="DANH SÁCH ROBOT" isLoading />
        <div className="mt-6 text-center">Đang tải...</div>
      </section>
    )
  }

  return (
    <>
      {contextHolder}
      <section className="space-y-4">
        <PageHeader
          title="DANH SÁCH ROBOT"
          onToggleFilter={() => setShowFilter((v) => !v)}
          onExport={canExport ? handleExport : undefined}
          disabled={!hasData}
          isExporting={isExporting}
        />

        {showFilter && (
          <RobotFilterPanel
            values={filter}
            onChange={setFilter}
            showUnitFilter={showUnitColumn}
            onSearch={() => {
              const trimmedFilter = {
                ...filter,
                keyword: filter.keyword.trim(),
              }
              setFilter(trimmedFilter)
              setActiveFilters(trimmedFilter)
              setPagination((s) => ({ ...s, pageIndex: 0 }))
              refetch()
            }}
          />
        )}

        <DataTable
          columns={columns}
          data={data?.items ?? []}
          tableOptions={{
            state: { pagination: { pageIndex, pageSize } },
            onPaginationChange: setPagination,
            getPaginationRowModel: getPaginationRowModel(),
            getCoreRowModel: getCoreRowModel(),
            manualPagination: true,
            pageCount: data?.totalPages,
          }}
          footer={
            <Pagination
              page={pageIndex + 1}
              pageCount={data?.totalPages ?? 1}
              pageSize={pageSize}
              totalCount={data?.total ?? 0}
              onPageChange={(p) =>
                setPagination((s) => ({ ...s, pageIndex: p - 1 }))
              }
              onPageSizeChange={(size) =>
                setPagination({ pageIndex: 0, pageSize: size })
              }
            />
          }
        />

        <RobotDetailDrawer
          robotId={viewingRobotId}
          onClose={() => setViewingRobotId(null)}
          showUnitColumn={showUnitColumn}
          onUpdate={(robot) => {
            setViewingRobotId(null)
            setEditingRobotId(robot.id)
          }}
        />
        <DrawerEditRobot
          visible={!!editingRobotId}
          robotId={editingRobotId || undefined}
          onClose={() => setEditingRobotId(null)}
          onSuccess={() => {
            setEditingRobotId(null)
            queryClient.invalidateQueries({ queryKey: ['robots'] })
          }}
        />

        {/* Modal xác nhận đổi trạng thái */}
        <ConfirmModal
          isOpen={!!pendingRobot}
          title="Xác nhận"
          description={
            <>
              Bạn chắc chắn muốn{' '}
              {pendingRobot?.status !== 1 ? 'vô hiệu hóa ' : 'kích hoạt '}
              robot <strong>{pendingRobot?.name}</strong>?
            </>
          }
          variant="info"
          cancelText="Hủy"
          confirmText="Đồng ý"
          isLoading={
            !!loadingRobotId &&
            !!pendingRobot &&
            loadingRobotId === pendingRobot.id
          }
          onClose={() => setPendingRobot(null)}
          onConfirm={() => {
            if (!pendingRobot) return
            toggleRobotStatus(pendingRobot)
            setPendingRobot(null)
          }}
        />

        {/* Modal xác nhận xóa robot */}
        <ConfirmModal
          isOpen={!!deletingRobot}
          title="Xác nhận xóa"
          description={
            <>
              Bạn có chắc chắn muốn xóa robot{' '}
              <strong>{deletingRobot?.name}</strong> không? Hành động này không
              thể hoàn tác.
            </>
          }
          variant="danger"
          cancelText="Hủy"
          confirmText="Xóa"
          isLoading={isDeleting}
          onClose={() => setDeletingRobot(null)}
          onConfirm={() => {
            if (!deletingRobot) return
            removeRobot(deletingRobot.id)
          }}
        />
      </section>
    </>
  )
}
